// @TechDebt(handsontable@13.1.0): UndoRedo.Action and plugin.done are runtime-only,
// not in the published .d.ts. The two `as any` casts below are the only type-system
// escapes. On upgrade, re-verify they exist; otherwise undo silently no-ops.
//
// Action fields must be JSON-safe: plugin.undo() deep-clones the action via
// JSON.parse(JSON.stringify(action)). A component reference would throw on circular
// refs through the Angular injector. Functions are silently dropped by stringify, so
// undoFn/redoFn closures safely close over the component.

import Handsontable from 'handsontable';
import type { Logger } from '../../../../services/logging.service';

export const REVIEW_ACTION_TYPES = {
  MERGE_ROWS: 'review.mergeRows',
  MERGE_COLUMNS: 'review.mergeColumns',
  REMOVE_ROWS: 'review.removeRows',
  REMOVE_COLUMNS: 'review.removeColumns',
} as const;

export interface ReviewEditorSnapshot {
  activePage: number;
  pageData: any[][];
  cells: any[][];
  statementOptionsPage: any[];
  metadataPage: any[][];
  selectionBefore: number[][] | null;
}

export interface SelectionRange {
  start: { row: number; col: number };
  end: { row: number; col: number };
}

// Mutable wrapper: insert helpers update the same selection the redoFn closure references.
export interface SelectionRef {
  selection: SelectionRange[];
}

export interface ReviewEditorActionOptions {
  actionType: string;
  snapshot: ReviewEditorSnapshot;
  undoFn: () => void;
  redoFn: () => void;
  selectionRef?: SelectionRef;
}

const HotActionBase: new () => { actionType?: string } =
  (Handsontable.plugins.UndoRedo as any).Action;

export class ReviewEditorAction extends HotActionBase {
  readonly actionType: string;
  readonly snapshot: ReviewEditorSnapshot;
  readonly selectionRef?: SelectionRef;
  private readonly undoFn: () => void;
  private readonly redoFn: () => void;

  constructor(opts: ReviewEditorActionOptions) {
    super();
    this.actionType = opts.actionType;
    this.snapshot = opts.snapshot;
    this.selectionRef = opts.selectionRef;
    this.undoFn = opts.undoFn;
    this.redoFn = opts.redoFn;
  }

  undo(_instance: Handsontable, undoneCallback: () => void): void {
    this.undoFn();
    undoneCallback();
  }

  redo(_instance: Handsontable, redoneCallback: () => void): void {
    this.redoFn();
    redoneCallback();
  }
}

export function pushReviewEditorAction(
  hot: Handsontable | undefined | null,
  makeAction: () => ReviewEditorAction,
  logger?: Logger,
): void {
  if (!hot) {
    return;
  }
  const plugin = hot.getPlugin('undoRedo') as any;
  if (!plugin || typeof plugin.done !== 'function') {
    logger?.error(
      'Handsontable UndoRedo.done is not available — review undo action not recorded. ' +
      'Likely a Handsontable version mismatch; see undo-actions.ts @TechDebt: note.',
      { hasPlugin: !!plugin, doneType: plugin && typeof plugin.done },
    );
    return;
  }
  plugin.done(makeAction);
}
